import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI } from '../services/api';
import { API, MEDIA_URL } from '../config';
import './JobDetail.css';

function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showPrepModal, setShowPrepModal] = useState(false);
  const [prepData, setPrepData] = useState(null);
  const [prepLoading, setPrepLoading] = useState(false);
  const tier = localStorage.getItem('user_tier') || 'free';
  const isPremium = tier === 'premium';
  const token = localStorage.getItem('token');
  const [applicationData, setApplicationData] = useState({
    fullName: '',
    email: '',
    phone: '',
    coverLetter: '',
    answers: {},
    documents: {
      cv: null,
      portfolio: null,
      coverLetter: null
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [reported, setReported] = useState(false);

  const handleReport = async () => {
    if (!token) { navigate('/login'); return; }
    const reason = window.prompt('Why are you reporting this job? (e.g., suspicious, fake, scam)');
    if (!reason) return;
    try {
      await fetch(`${API}/jobs/${jobId}/report/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      setReported(true);
    } catch {}
  };

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await jobsAPI.getJob(jobId);
      setJob(response.data);
      
      // Initialize answers object for screening questions
      if (response.data.screening_questions) {
        const initialAnswers = {};
        response.data.screening_questions.forEach((_, index) => {
          initialAnswers[index] = '';
        });
        setApplicationData(prev => ({ ...prev, answers: initialAnswers }));
      }
    } catch (error) {
      console.error('Failed to fetch job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationData(prev => ({ ...prev, [name]: value }));
  };

  const handleAnswerChange = (index, value) => {
    setApplicationData(prev => ({
      ...prev,
      answers: { ...prev.answers, [index]: value }
    }));
  };

  const handleFileChange = (e, docType) => {
    const file = e.target.files[0];
    setApplicationData(prev => ({
      ...prev,
      documents: { ...prev.documents, [docType]: file }
    }));
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new FormData();
      formData.append('job_id', jobId);
      formData.append('applicant_name', applicationData.fullName);
      formData.append('applicant_email', applicationData.email);
      formData.append('applicant_phone', applicationData.phone);
      
      // Add screening answers
      if (job.screening_questions && job.screening_questions.length > 0) {
        const answersArray = job.screening_questions.map((question, index) => ({
          question: question,
          answer: applicationData.answers[index] || ''
        }));
        formData.append('screening_answers', JSON.stringify(answersArray));
      }
      
      // Add documents
      if (applicationData.documents.cv) {
        formData.append('cv', applicationData.documents.cv);
      }
      if (applicationData.documents.portfolio) {
        formData.append('portfolio', applicationData.documents.portfolio);
      }
      if (applicationData.coverLetter) {
        // Create a text file for cover letter
        const coverLetterBlob = new Blob([applicationData.coverLetter], { type: 'text/plain' });
        formData.append('cover_letter', coverLetterBlob, 'cover_letter.txt');
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/applications/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit application');
      }
      
      setSubmitMessage('Application submitted successfully! The recruiter will contact you soon.');
      setTimeout(() => {
        navigate('/jobs');
      }, 3000);
    } catch (error) {
      console.error('Application error:', error);
      setSubmitMessage(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-container">Loading job details...</div>;
  }

  if (!job) {
    return <div className="error-container">Job not found</div>;
  }

  return (
    <div className="job-detail-container">
      <nav className="job-detail-nav">
        <button onClick={() => navigate('/jobs')} className="back-button">
          <i className="fas fa-arrow-left"></i> Back to Jobs
        </button>
      </nav>

      <div className="job-detail-content">
        {/* Job Header */}
        <div className="job-detail-header">
          <div className="job-title-section">
            {job.company_logo && (
              <img
                src={job.company_logo.startsWith('http') ? job.company_logo : `${MEDIA_URL}${job.company_logo}`}
                alt={job.company}
                className="job-detail-company-logo"
                onClick={() => navigate(`/company/${encodeURIComponent(job.company)}`)}
                style={{ cursor: 'pointer' }}
                title={`View ${job.company} profile`}
              />
            )}
            <h1>{job.title}</h1>
            <div className="job-company-info">
              <i className="fas fa-building"></i>
              <span
                className="company-name"
                onClick={() => navigate(`/company/${encodeURIComponent(job.company)}`)}
                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                title={`View ${job.company} profile`}
              >{job.company}</span>
              {job.department && <span className="department"> • {job.department}</span>}
            </div>
          </div>
          <span className="employment-badge">{job.employment_type || job.job_type || 'Full-time'}</span>
        </div>

        {/* Quick Info Bar */}
        <div className="job-quick-info">
          {job.city && job.country && (
            <div className="info-item">
              <i className="fas fa-map-marker-alt"></i>
              {job.city}, {job.country}
            </div>
          )}
          {job.work_mode && (
            <div className="info-item">
              <i className="fas fa-laptop-house"></i>
              {job.work_mode}
            </div>
          )}
          {(job.salary_min || job.salary_max || job.salary) && (
            <div className="info-item">
              <i className="fas fa-money-bill-wave"></i>
              {job.salary || `${job.salary_currency || 'ZAR'} ${job.salary_min?.toLocaleString()} - ${job.salary_max?.toLocaleString()}`}
              {job.salary_negotiable && ' (Negotiable)'}
            </div>
          )}
          {job.experience_level && (
            <div className="info-item">
              <i className="fas fa-chart-line"></i>
              {job.experience_level}
            </div>
          )}
          {job.application_deadline && (
            <div className="info-item deadline">
              <i className="fas fa-clock"></i>
              Apply by: {new Date(job.application_deadline).toLocaleDateString()}
            </div>
          )}
        </div>

        {/* Job Details Sections */}
        <div className="job-sections">
          {/* Description */}
          {job.description && (
            <section className="job-section">
              <h2><i className="fas fa-info-circle"></i> About the Role</h2>
              <p className="section-content">{job.description}</p>
            </section>
          )}

          {/* Responsibilities */}
          {job.responsibilities && (
            <section className="job-section">
              <h2><i className="fas fa-tasks"></i> Responsibilities</h2>
              <div className="section-content list-content">
                {job.responsibilities.split('\n').filter(line => line.trim()).map((line, idx) => (
                  <div key={idx} className="list-item">{line}</div>
                ))}
              </div>
            </section>
          )}

          {/* Requirements */}
          {job.requirements && (
            <section className="job-section">
              <h2><i className="fas fa-check-circle"></i> Required Qualifications</h2>
              <div className="section-content list-content">
                {job.requirements.split('\n').filter(line => line.trim()).map((line, idx) => (
                  <div key={idx} className="list-item">{line}</div>
                ))}
              </div>
            </section>
          )}

          {/* Preferred Skills */}
          {job.preferred_skills && (
            <section className="job-section">
              <h2><i className="fas fa-star"></i> Preferred Skills</h2>
              <div className="section-content list-content">
                {job.preferred_skills.split('\n').filter(line => line.trim()).map((line, idx) => (
                  <div key={idx} className="list-item">{line}</div>
                ))}
              </div>
            </section>
          )}

          {/* Benefits */}
          {job.benefits && job.benefits.length > 0 && (
            <section className="job-section">
              <h2><i className="fas fa-gift"></i> Benefits</h2>
              <div className="benefits-tags">
                {job.benefits.map((benefit, idx) => (
                  <span key={idx} className="benefit-tag">{benefit}</span>
                ))}
              </div>
            </section>
          )}

          {/* Education */}
          {job.education_requirement && (
            <section className="job-section">
              <h2><i className="fas fa-graduation-cap"></i> Minimum Education Requirement</h2>
              <p className="section-content">{job.education_requirement}</p>
            </section>
          )}

          {/* Apply Button at Bottom */}
          {!showApplicationForm && (
            <div className="apply-button-bottom-container" style={{ gap: '1rem' }}>
              {isPremium && (
                <button className="apply-button-main" style={{ background: 'linear-gradient(135deg, #5d1a70, #640ff7)' }}
                  onClick={async () => {
                    setShowPrepModal(true);
                    if (!prepData) {
                      setPrepLoading(true);
                      try {
                        const res = await fetch(`${API}/insights/interview-prep?job_id=${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
                        const d = await res.json();
                        if (res.ok) setPrepData(d);
                      } catch {}
                      finally { setPrepLoading(false); }
                    }
                  }}>
                  <i className="fas fa-lightbulb"></i> Interview Prep
                </button>
              )}
              <button className="apply-button-main" onClick={() => setShowApplicationForm(true)}>
                <i className="fas fa-paper-plane"></i> Apply for this Position
              </button>
              <button
                onClick={handleReport}
                disabled={reported}
                style={{
                  background: 'none', border: '1px solid var(--border)', borderRadius: '8px',
                  padding: '0.6rem 1rem', color: reported ? 'var(--text-muted)' : 'var(--danger)',
                  cursor: reported ? 'default' : 'pointer', fontSize: '0.85rem', display: 'flex',
                  alignItems: 'center', gap: '0.4rem'
                }}
              >
                <i className="fas fa-flag"></i>
                {reported ? 'Reported' : 'Report Job'}
              </button>
            </div>
          )}
        </div>

        {/* Application Form */}
        {showApplicationForm && (
          <div className="application-form-container">
            <h2 className="form-title">
              <i className="fas fa-file-alt"></i> Application Form
            </h2>
            
            <form onSubmit={handleSubmitApplication} className="application-form">
              {/* Personal Information */}
              <div className="form-section">
                <h3>Personal Information</h3>
                
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={applicationData.fullName}
                    onChange={handleInputChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={applicationData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={applicationData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+27 123 456 789"
                    />
                  </div>
                </div>
              </div>

              {/* Required Documents */}
              {job.required_documents && job.required_documents.length > 0 && (
                <div className="form-section">
                  <h3>Required Documents</h3>
                  
                  {job.required_documents.includes('CV') && (
                    <div className="form-group">
                      <label>Upload CV/Resume *</label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => handleFileChange(e, 'cv')}
                        required
                      />
                      <small>PDF, DOC, or DOCX (Max 5MB)</small>
                    </div>
                  )}

                  {job.required_documents.includes('Portfolio') && (
                    <div className="form-group">
                      <label>Upload Portfolio *</label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => handleFileChange(e, 'portfolio')}
                        required
                      />
                      <small>PDF (Max 10MB)</small>
                    </div>
                  )}

                  {job.required_documents.includes('Cover letter') && (
                    <div className="form-group">
                      <label>Cover Letter *</label>
                      <textarea
                        name="coverLetter"
                        value={applicationData.coverLetter}
                        onChange={handleInputChange}
                        required
                        rows="6"
                        placeholder="Tell us why you're a great fit for this role..."
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Screening Questions */}
              {job.screening_questions && job.screening_questions.length > 0 && (
                <div className="form-section">
                  <h3>Screening Questions</h3>
                  
                  {job.screening_questions.map((question, index) => (
                    <div key={index} className="form-group">
                      <label>
                        <span className="question-number">{index + 1}.</span> {question} *
                      </label>
                      <textarea
                        value={applicationData.answers[index] || ''}
                        onChange={(e) => handleAnswerChange(index, e.target.value)}
                        required
                        rows="3"
                        placeholder="Your answer..."
                      />
                    </div>
                  ))}
                </div>
              )}

              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('success') ? 'success' : 'error'}`}>
                  {submitMessage}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setShowApplicationForm(false)}
                  className="btn-cancel"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default JobDetail;
